/**
 * lib/platter.ts — what a finger on the record means: a speed, and an angle.
 *
 * Task 09. `spec.md` «Пластинка», the table of the first version's gesture:
 *
 * | Палец | Скорость воспроизведения |
 * |---|---|
 * | не касается | 1,0 |
 * | лежит и не движется (спустя 120 мс) | 0 — пластинка стоит |
 * | тянет по кругу медленнее нормы | от 0,55 до 1,0 по скорости пальца |
 * | крутит быстрее нормы | от 1,0 до 2,0 |
 * | движется против хода | как «стоит» (0) |
 * | отпустил | плавный возврат к 1,0 за 0,3 с |
 *
 * This module turns pointer positions into those speeds and hands them to `onRate`; it knows
 * nothing of the element (`engine/speed.ts` writes it, and does the return to 1,0) nor of the
 * page (`components/Vinyl.tsx` feeds it the pointer).
 *
 * ## The speed is the finger's, measured against the record's own
 *
 * A record turns at 45 a minute, {@link NOMINAL_DEG_PER_S} degrees a second. A finger turning
 * it at half that plays it at half speed, as a hand on a real platter would — so the speed is
 * the finger's angular speed over the nominal one, clamped to what the element can sound:
 * {@link MIN_RATE} below, {@link MAX_RATE} above. The spec's two ranges, «от 0,55 до 1,0» and
 * «от 1,0 до 2,0», are the two sides of that one line.
 *
 * ## Why the speed is read over a window, and why a slow finger is a still one
 *
 * A phone sends 60–120 moves a second, each a few pixels, with timestamps that jitter. The
 * speed of one move against the last is noise; the travel over the last {@link WINDOW_MS} is
 * not. And a thumb resting on glass is never quite still: it trembles by a pixel or two. A
 * tremor read as movement is the defect the old project shipped — a resting thumb that played
 * the track slowly instead of stopping it — so a finger slower than {@link REST_SHARE} of the
 * record's speed counts as resting, and after {@link STILL_AFTER_MS} of rest the record stops.
 * A speed over less than {@link MIN_SPAN_MS} of movement is not read at all: the first
 * twitch of a thumb that has just landed is exactly such a tremor, and read over its 8 ms it
 * was a drag at 0,55 (found by the test «палец дрожит на месте»).
 */

/** One turn of the record takes this long: 45 turns a minute. */
export const TURN_S = 60 / 45;

/** The record's own speed, in degrees a second. */
export const NOMINAL_DEG_PER_S = 360 / TURN_S;

/** WebKit renders nothing at 0.5 and below (`spec.md` «Пластинка»), so a slow drag stops here. */
export const MIN_RATE = 0.55;

/** The fastest a finger can play the record. */
export const MAX_RATE = 2;

/** A finger that has not moved for this long is holding the record still. */
export const STILL_AFTER_MS = 120;

/** The finger's speed is its travel over this much of the recent past. */
export const WINDOW_MS = 100;

/**
 * …and is not read at all over less than this. The first move of a landed finger is 8 ms
 * after it landed, and a tremor of half a degree over 8 ms reads as a brisk drag.
 */
export const MIN_SPAN_MS = 50;

/** Slower than this share of the record's own speed, a finger is resting, not turning. */
export const REST_SHARE = 0.1;

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** The angle of `point` about `centre`, in degrees. Clockwise grows: `y` points down. */
export function angleAt(point: Point, centre: Point): number {
  return (Math.atan2(point.y - centre.y, point.x - centre.x) * 180) / Math.PI;
}

/** A difference of two angles as the short way round, in (-180, 180]. */
export function shortestArc(deg: number): number {
  const turned = (((deg + 180) % 360) + 360) % 360;
  return turned === 0 ? 180 : turned - 180;
}

/**
 * The speed for a finger turning the record at `share` of its own speed: `0` for a finger
 * going backwards or not at all, otherwise the share itself within what the element sounds.
 */
export function rateForShare(share: number): number {
  if (!(share > 0)) return 0;
  return Math.min(MAX_RATE, Math.max(MIN_RATE, share));
}

export interface PlatterDeps {
  /** The speed the finger asks for. */
  readonly onRate: (rate: number) => void;
  /**
   * Task 21: the record's own speed, in degrees a second — 270 at 45 ({@link NOMINAL_DEG_PER_S}),
   * 200 at 33 (`lib/records.ts`). A finger is measured against the record that is on the deck.
   */
  readonly nominalDegPerS?: number;
  /** How far the finger has turned the record since it landed, in degrees, clockwise. */
  readonly onTurn: (travel: number) => void;
  /** Milliseconds, from any fixed origin. */
  readonly now: () => number;
}

export interface PlatterGesture {
  /** The finger landed at `point` on a record centred at `centre`. */
  down(point: Point, centre: Point): void;
  /** The finger is at `point` now. */
  move(point: Point): void;
  /** The finger left, by any way out. Safe to call when it is not down. */
  end(): void;
}

/** Where the finger had got to, and when: its travel is the sum of its steps, in degrees. */
interface Sample {
  readonly t: number;
  readonly travel: number;
}

export function createPlatterGesture(deps: PlatterDeps): PlatterGesture {
  const nominal = deps.nominalDegPerS ?? NOMINAL_DEG_PER_S;
  let stillTimer: ReturnType<typeof setTimeout> | undefined;
  let centre: Point = { x: 0, y: 0 };
  let lastAngle = 0;
  let travel = 0;
  let samples: Sample[] = [];

  function clearStill(): void {
    if (stillTimer !== undefined) clearTimeout(stillTimer);
    stillTimer = undefined;
  }

  /** From now, {@link STILL_AFTER_MS} without movement stops the record. */
  function armStill(): void {
    clearStill();
    stillTimer = setTimeout(() => {
      stillTimer = undefined;
      deps.onRate(0);
    }, STILL_AFTER_MS);
  }

  /**
   * The finger's speed as a share of the record's. Measured from the oldest move inside the
   * window — or from the move before this one, when even that is older: a finger that rested
   * and moves again is measured from where it rested, not from a second ago.
   */
  function share(t: number): number | undefined {
    samples.push({ t, travel });
    while (samples.length > 2 && (samples[0]?.t ?? t) < t - WINDOW_MS) samples.shift();
    const from = samples[0];
    if (from === undefined || t - from.t < MIN_SPAN_MS) return undefined;
    const degPerS = ((travel - from.travel) * 1000) / (t - from.t);
    return degPerS / nominal;
  }

  return {
    down(point: Point, at: Point): void {
      centre = at;
      lastAngle = angleAt(point, centre);
      travel = 0;
      samples = [{ t: deps.now(), travel: 0 }];
      armStill();
    },

    move(point: Point): void {
      const angle = angleAt(point, centre);
      const step = shortestArc(angle - lastAngle);
      lastAngle = angle;
      // A move with no travel — a change of pressure, a repeated event — is not movement.
      if (step === 0) return;
      travel += step;
      deps.onTurn(travel);
      const measured = share(deps.now());
      if (measured === undefined) return;
      if (measured >= REST_SHARE) {
        deps.onRate(rateForShare(measured));
        armStill();
      } else if (measured <= -REST_SHARE) {
        // Backwards is silence at once: the element cannot play a record in reverse.
        deps.onRate(0);
        armStill();
      }
      // A tremor below the rest share asks for nothing: the stillness timer runs on.
    },

    end(): void {
      clearStill();
    },
  };
}
